const ROLE_ORDER = ['admin', 'committee', 'user']
const DEFAULT_ROLE = 'user'
const MULTIUSER_ROLE = 'multiuser'

const ROLE_DEFINITIONS = {
  admin: {
    label: 'Administrator',
    aliases: ['admin', 'administrator', 'mca admin', 'mca administrator', 'mca system admin', 'system admin', 'system administrator'],
    landingPath: '/dashboard/admin',
    sectionTitle: 'Admin'
  },
  committee: {
    label: 'Committee',
    aliases: ['committee', 'committe', 'mca committee'],
    landingPath: '/dashboard/committee',
    sectionTitle: 'Committee'
  },
  user: {
    label: 'User',
    aliases: ['user', 'mca user'],
    landingPath: '/dashboard',
    sectionTitle: 'User'
  },
  multiuser: {
    label: 'Multi User',
    aliases: ['multiuser'],
    landingPath: '/dashboard',
    sectionTitle: 'Workspace'
  }
}

const ROUTE_ROLE_RULES = [
  { prefixes: ['/notifications'], roles: ROLE_ORDER },
  {
    prefixes: [
      '/dashboard/admin',
      '/agreements',
      '/audit-log',
      '/review-queue',
      '/translation-requests',
      '/reports',
      '/permission',
      '/security/permissions',
      '/security/audit',
      '/operations',
      '/accounts',
      '/config',
      '/setting',
      '/ocr'
    ],
    roles: ['admin']
  },
  { prefixes: ['/dashboard/committee', '/review-history', '/review', '/guideline', '/help'], roles: ['committee'] },
  {
    prefixes: [
      '/dashboard/user',
      '/dashboard/user-agreements',
      '/dashboard/user-clause-library',
      '/dashboard/user-review-queue',
      '/dashboard/user-translation',
      '/dashboard/user-reports',
      '/dashboard/user-guideline'
    ],
    roles: ['user']
  },
  { prefixes: ['/clause-library'], roles: ['admin', 'committee'] },
  { prefixes: ['/general/profile', '/general/preferences'], roles: ['committee', 'user'] },
  { prefixes: ['/dashboard', '/logout'], roles: ROLE_ORDER }
]

const NAVIGATION_BLUEPRINTS = {
  admin: [
    { kind: 'title', value: 'MOU Administration' },
    { kind: 'item', name: 'All Agreements', to: '/agreements', icon: 'cil-file' },
    { kind: 'item', name: 'Audit Log', to: '/audit-log', icon: 'cil-notes' },
    { kind: 'item', name: 'Clause Library', to: '/clause-library', icon: 'cil-book' },
    { kind: 'item', name: 'Review Queue', to: '/review-queue', icon: 'cil-list', badgeKey: 'adminReviewQueue' },
    { kind: 'item', name: 'Translation Requests', to: '/translation-requests', icon: 'cil-speech', badgeKey: 'adminTranslationQueue', badgeColor: 'warning' },
    { kind: 'item', name: 'Reports & Analytics', to: '/reports', icon: 'cil-chart-pie' },
    { kind: 'title', value: 'MCA Modules' },
    { kind: 'item', name: 'MCA Registry', to: '/mca/registry', icon: 'cil-description' },
    { kind: 'item', name: 'MCA Operations', to: '/operations/business', icon: 'cil-layers' },
    { kind: 'item', name: 'MCA Account Directory', to: '/accounts/directory', icon: 'cil-user' },
    { kind: 'title', value: 'System Administration' },
    {
      kind: 'dropdown',
      name: 'Permission',
      route: '/permission',
      icon: 'cil-lock-locked',
      items: [
        { name: 'Create Menu', to: '/security/permissions/menu' },
        { name: 'Create Group', to: '/security/permissions/group' },
        { name: 'MCA Permission Matrix', to: '/security/permissions/matrix' },
        { name: 'Audit Explorer', to: '/security/audit' }
      ]
    },
    {
      kind: 'dropdown',
      name: 'Config',
      route: '/config',
      icon: 'cil-settings',
      items: [
        { name: 'Message Authen', to: '/config/message-authen' },
        { name: 'Email Notifications', to: '/config/email-notifications' },
        { name: 'Workflow Actions', to: '/config/workflow-actions' },
        { name: 'Runtime Access', to: '/config/runtime-access' },
        { name: 'Database Backup', to: '/config/database-backup' },
        { name: 'Setting Message', to: '/config/setting-message' },
        { name: 'Setting Verification', to: '/config/verification' }
      ]
    },
    {
      kind: 'dropdown',
      name: 'Setting',
      route: '/setting',
      icon: 'cil-list',
      items: [
        { name: 'Setting Group', to: '/setting/group' },
        { name: 'Message Status', to: '/setting/message-status' }
      ]
    }
  ],
  committee: [
    { kind: 'title', value: 'Committee' },
    { kind: 'item', name: 'Review', to: '/review?workspace=committee', icon: 'cil-check-circle', badgeKey: 'committeeReviewQueue' },
    { kind: 'item', name: 'Review History', to: '/review-history?workspace=committee', icon: 'cil-history' },
    { kind: 'item', name: 'Clause Library', to: '/clause-library?workspace=committee', icon: 'cil-book' },
    { kind: 'item', name: 'User Manual', to: '/guideline?workspace=committee', icon: 'cil-book' },
    { kind: 'item', name: 'Help & Support', to: '/help?workspace=committee', icon: 'cil-life-ring' },
    {
      kind: 'dropdown',
      name: 'General',
      route: '/general',
      icon: 'cil-settings',
      items: [
        { name: 'Profile', to: '/general/profile?workspace=committee' },
        { name: 'Preferences', to: '/general/preferences?workspace=committee' }
      ]
    }
  ],
  user: [
    { kind: 'title', value: 'User' },
    { kind: 'item', name: 'My Agreements', to: '/dashboard/user-agreements', icon: 'cil-file' },
    { kind: 'item', name: 'My Clause Library', to: '/dashboard/user-clause-library', icon: 'cil-book' },
    { kind: 'item', name: 'My Review Queue', to: '/dashboard/user-review-queue', icon: 'cil-list', badgeKey: 'userReviewQueue' },
    { kind: 'item', name: 'My Translation Requests', to: '/dashboard/user-translation', icon: 'cil-speech', badgeKey: 'userTranslationQueue', badgeColor: 'warning' },
    { kind: 'item', name: 'My Reports', to: '/dashboard/user-reports', icon: 'cil-chart-pie' },
    { kind: 'item', name: 'User Manual', to: '/dashboard/user-guideline', icon: 'cil-book' },
    {
      kind: 'dropdown',
      name: 'General',
      route: '/general',
      icon: 'cil-settings',
      items: [
        { name: 'Profile', to: '/general/profile' },
        { name: 'Preferences', to: '/general/preferences' }
      ]
    }
  ]
}

export function normalizePath (value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const [path] = raw.split('?')
  return path || ''
}

export function normalizeRoleToken (value) {
  const normalized = String(value || '').trim().toLowerCase()
  if (!normalized) return ''
  const matched = Object.entries(ROLE_DEFINITIONS).find(([, definition]) => {
    return Array.isArray(definition.aliases) && definition.aliases.includes(normalized)
  })
  return matched ? matched[0] : ''
}

export function collectRoleTokens (value, target = new Set()) {
  if (!value) return target
  if (typeof value === 'string') {
    const normalized = normalizeRoleToken(value)
    if (normalized) target.add(normalized)
    return target
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectRoleTokens(item, target))
    return target
  }
  if (typeof value === 'object') {
    ;[
      value.name,
      value.title,
      value.value,
      value.code,
      value.key,
      value.slug,
      value.role,
      value.roleName,
      value.label,
      value.groupLabel,
      value.visibleTypeName,
      value.descriptionText
    ].forEach(item => collectRoleTokens(item, target))
  }
  return target
}

export function resolveRolesFromValue (value, fallback = []) {
  const tokens = collectRoleTokens(value)
  const roles = ROLE_ORDER.filter(role => tokens.has(role))
  return roles.length ? roles : fallback
}

export function inferRolesFromPath (value, fallback = ROLE_ORDER) {
  const path = normalizePath(value)
  if (!path) return fallback.slice()
  const match = ROUTE_ROLE_RULES.find((rule) => {
    return (rule.prefixes || []).some(prefix => path === prefix || path.startsWith(`${prefix}/`))
  })
  return match ? match.roles.slice() : fallback.slice()
}

export function getRoleLandingPath (role) {
  const key = role === MULTIUSER_ROLE ? MULTIUSER_ROLE : normalizeRoleToken(role)
  return (ROLE_DEFINITIONS[key] && ROLE_DEFINITIONS[key].landingPath) || ROLE_DEFINITIONS[DEFAULT_ROLE].landingPath
}

export function getRoleLabel (role) {
  const key = role === MULTIUSER_ROLE ? MULTIUSER_ROLE : normalizeRoleToken(role)
  return (ROLE_DEFINITIONS[key] && ROLE_DEFINITIONS[key].label) || ROLE_DEFINITIONS[DEFAULT_ROLE].label
}

export function getRoleOrder () {
  return ROLE_ORDER.slice()
}

export function getDefaultRole () {
  return DEFAULT_ROLE
}

export function getMultiuserRole () {
  return MULTIUSER_ROLE
}

export function getNavigationBlueprint (role) {
  const normalized = normalizeRoleToken(role)
  const items = NAVIGATION_BLUEPRINTS[normalized] || []
  return items.map(item => ({
    ...item,
    items: Array.isArray(item.items) ? item.items.map(child => ({ ...child })) : item.items
  }))
}
